/**
 * 危機管理水位グラフ生成
 */
import color from "../../scss/color.scss";
import libCG, { str } from "./CommonGraph";

export default {
  name: "lib-swstage-graph",
  getSvgGraph,
  getSvgLeftLabel,
  getSvgRightLabel,
  getSvgLegend,
  getImageGraph,
};

// グラフ描画領域上余白
const paddingTop = 40;
// グラフ描画領域下余白
const paddingBottom = 120;
// ラベル描画領域左余白
const paddingLeft = 50;
// 危機管理水位ラベル分割数
const stageDivY = 8;
// ラベル表示間隔
const spaceY = 2;
// 危機管理水位ラベル桁数（小数点以下）
const stageLabelDisit = 1;

// 凡例サイズ
const legendWidth = 660;
const legendHeight = 385;
const legendPadding = 10;

// 「河川の水位」文字列
const strStageTitle = "河川の水位";
// 「水面の位置」文字列
const strStageLevel = "水面の位置";
// 「氾濫開始水位」文字列
const strStageFlood = "氾濫開始水位";
// 「危険水位」文字列
const strStageDanger = "危険水位";
// 「観測開始水位」文字列
const strStageStart = "水位計測中";

// 見出し文字サイズ
const baseStageTitleSize = 60;
const baseStageTitleWeight = 500;

// グラフのスタイル
const styleGraph = `<![CDATA[
  .kwg-waterlevel-normal {
    fill: ${color.tmSwstgNormal};
    stroke: ${color.tmSwstgNormal};
    stroke-width: 13;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .kwg-waterlevel-flood {
    fill: ${color.tmSwstgFlood};
    stroke: ${color.tmSwstgFlood};
    stroke-width: 13;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .kwg-waterlevel-flood-border {
    fill: null;
    stroke: ${color.tmSwstgFlood};
    stroke-width: 5;
  }
  .kwg-waterlevel-danger {
    fill: ${color.tmSwstgDanger};
    stroke: ${color.tmSwstgDanger};
    stroke-width: 13;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .kwg-waterlevel-danger-border {
    fill: null;
    stroke: ${color.tmSwstgDanger};
    stroke-width: 5;
  }
  .kwg-waterlevel-start {
    fill: ${color.tmSwstgStart};
    stroke: ${color.tmSwstgStart};
    stroke-width: 13;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .kwg-waterlevel-start-border {
    fill: null;
    stroke: ${color.tmSwstgStart};
    stroke-width: 5;
  }
]]>`;
// 左ラベルのスタイル
const styleLeftLabel = `<![CDATA[
  .kwg-waterlevel-label {
    fill: darkblue;
    font-weight: bold;
    font-size: 48px;
  }
  .kwg-waterlevel-title {
    fill: darkblue;
    font-weight: TITLESTAGEWEIGHT;
    font-size: TITLESTAGESIZE;
  }
  .kwg-waterlevel-flood-marker {
    fill: ${color.tmSwstgFlood};
    stroke: ${color.tmSwstgFlood};
    stroke-width: 1;
  }
  .kwg-waterlevel-danger-marker {
    fill: ${color.tmSwstgDanger};
    stroke: ${color.tmSwstgDanger};
    stroke-width: 1;
  }
  .kwg-waterlevel-start-marker {
    fill: ${color.tmSwstgStart};
    stroke: ${color.tmSwstgStart};
    stroke-width: 1;
  }
]]>`;
// 凡例のスタイル
const styleLegend = `<![CDATA[
  .kwg-legend-rect {
    fill: white;
    stroke: dodgerblue;
    stroke-width: 9;
    opacity: 0.9;
  }
  .kwg-legend-label {
    fill: black;
    font-weight: 500;
    font-size: 54px;
  }
  .kwg-legend-label-unit {
    fill: black;
    font-weight: 500;
    font-size: 36px;
  }
]]>`;

/**
 * SVGグラフ取得
 * @param obsInfo 観測所情報
 *    value[]: 観測値
 *      date: 観測日
 *      time: 観測時間
 *      level: 水位レベル
 *    spec: 観測情報
 *      level: 水位レベル（堤防天端を0.0mとした水位）
 *        max: 水位最大値
 *        flood: 氾濫開始水位（通常は0.0m）
 *        danger: 危険水位
 *        start: 観測開始水位
 *        min: 水位最小値（通常は河床-1.0m）
 *        title: 水位レベルラベル
 *        unit: 水位レベル単位
 *        obsCycl:観測周期(分)
 * @param viewProp 描画プロパティ
 *    width: 親要素の幅
 *    height: 親要素の高さ
 *    isExp: 拡大/全体表示（true=拡大, false=全体）
 * @return SVGグラフ
 */
function getSvgGraph(obsInfo, viewProp) {
  // svgタグ生成
  let svgGraph = libCG.makeGraphUnevenSvgTag(
    viewProp.width,
    viewProp.height,
    viewProp.isExp,
    obsInfo.value.length
  );
  // gタグ追加
  addGGraph(svgGraph, obsInfo, viewProp, false, 0);

  return svgGraph.outerHTML;
}

/**
 * グラフグループ追加
 * @param {*} parent 親要素
 * @param {*} obsInfo 観測所情報
 * @param {*} viewProp 描画プロパティ
 * @param {*} isSvg 画像提供モード
 * @param {*} offsetY 余白の高さ
 */
function addGGraph(parent, obsInfo, viewProp, isSvg, offsetY) {
  // gタグ追加
  let g = libCG.addGraphGroupTag(parent);
  // styleタグ追加
  libCG.addStyleTag(g, styleGraph);
  // グラフ表示範囲
  let x = 0;
  let y = paddingTop;
  let w = libCG.computeGraphUnevenWidth(
    isSvg ? false : viewProp.isExp,
    obsInfo.value.length
  );
  let h =
    libCG.computeHeight(viewProp.width, viewProp.height) -
    paddingTop -
    paddingBottom -
    offsetY;
  // 時刻ラベル追加
  libCG.addGraphXAxisLabelUnevenTag(
    g,
    x,
    y + h,
    w,
    obsInfo.value,
    isSvg ? false : viewProp.isExp
  );

  // 危機管理水位グラフ
  y = paddingTop;
  h =
    libCG.computeHeight(viewProp.width, viewProp.height) -
    paddingTop -
    paddingBottom -
    offsetY;
  // 危機管理水位フレーム追加
  libCG.addGraphFrameUnevenTag(
    g,
    x,
    y,
    w,
    h,
    obsInfo.value,
    viewProp.isExp,
    stageDivY,
    0
  );
  // 危機管理水位ツールチップ追加
  addSwstageGraphTooltip(g, x, y, w, h, obsInfo);
  // 危機管理水位グラフ追加
  addSwstageGraphLine(g, x, y, w, h, obsInfo);
  // 危機管理水位ボーダーライン追加
  addSwstageGraphBorder(g, x, y, w, h, obsInfo);

  return g;
}

/**
 * 危機管理水位ツールチップタグ追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ描画領域幅
 * @param {*} height グラフ描画領域高さ
 * @param {*} obsInfo 観測所情報
 */
function addSwstageGraphTooltip(parent, left, top, width, height, obsInfo) {
  let xs = width / (obsInfo.value.length + 1);
  let x = left + width;
  let w = xs - 2;
  let tag;
  let data;

  for (let index = 0; index < obsInfo.value.length; index++) {
    data = obsInfo.value[index];
    x = x - xs;
    if (data != null) {
      tag = libCG.makeToolTipSvgTag(
        x + 1,
        top,
        w,
        height,
        "kwg-tip",
        makeSwstageTip(obsInfo, data, index)
      );
      parent.appendChild(tag);
    }
  }
}

/**
 * 危機管理水位線グラフタグ追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ描画領域幅
 * @param {*} height グラフ描画領域高さ
 * @param {*} obsInfo 観測所情報
 */
function addSwstageGraphLine(parent, left, top, width, height, obsInfo) {
  let cls = "kwg-waterlevel-normal";
  let spec = obsInfo.spec.level;
  let max = spec.max;
  let min = spec.min;

  let bottom = top + height;
  let before = 0;
  let after = 1;
  let xs = width / (obsInfo.value.length + before + after);
  let x = left + width - xs * before;
  let ys = height / (max - min);
  let x1, x2, y1, y2;
  let tag;
  let current, next;

  for (let index = 0; index < obsInfo.value.length - 1; index++) {
    current = obsInfo.value[index];
    next = obsInfo.value[index + 1];
    x = x - xs;

    if (
      current == null ||
      current.level == null ||
      current.level > max ||
      current.level < min
    ) {
      continue;
    }

    if (
      next == null ||
      next.level == null ||
      next.level > max ||
      next.level < min
    ) {
      next = null;
    }

    if (spec.flood != null && current.level >= spec.flood) {
      cls = "kwg-waterlevel-flood";
    } else if (spec.danger != null && current.level >= spec.danger) {
      cls = "kwg-waterlevel-danger";
    } else if (spec.start != null && current.level >= spec.start) {
      cls = "kwg-waterlevel-start";
    } else {
      cls = "kwg-waterlevel-normal";
    }

    x1 = x - 1;
    y1 = bottom - ys * (current.level - min);

    // 次の観測日時が観測周期内の場合のみ線を描画
    if (next != null && spec.obsCycl) {
      const currentDate = new Date(current.date + " " + current.time);
      const nextDate = new Date(next.date + " " + next.time);
      const intervalMin = Math.floor(
        (currentDate.getTime() - nextDate.getTime()) / (1000 * 60)
      );
      if (intervalMin <= spec.obsCycl) {
        x2 = x - xs + 1;
        y2 = bottom - ys * (next.level - min);
        tag = libCG.makeLineGraphSvgTag(
          x1,
          y1,
          x2,
          y2,
          cls,
          makeSwstageTip(obsInfo, next, index + 1)
        );
        parent.appendChild(tag);
      }
    }

    tag = libCG.makeCircleSvgTag(
      x1,
      y1,
      11,
      cls,
      makeSwstageTip(obsInfo, current, index)
    );
    parent.appendChild(tag);
  }

  current = obsInfo.value[obsInfo.value.length - 1];
  if (
    current != null &&
    current.level != null &&
    current.level <= max &&
    current.level >= min
  ) {
    if (spec.flood != null && current.level >= spec.flood) {
      cls = "kwg-waterlevel-flood";
    } else if (spec.danger != null && current.level >= spec.danger) {
      cls = "kwg-waterlevel-danger";
    } else if (spec.start != null && current.level >= spec.start) {
      cls = "kwg-waterlevel-start";
    } else {
      cls = "kwg-waterlevel-normal";
    }

    x1 = x - xs - 1;
    y1 = bottom - ys * (current.level - min);

    tag = libCG.makeCircleSvgTag(
      x1,
      y1,
      11,
      cls,
      makeSwstageTip(obsInfo, current, obsInfo.value.length - 1)
    );
    parent.appendChild(tag);
  }
}

/**
 * 危機管理水位ボーダーラインタグ追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ描画領域幅
 * @param {*} height グラフ描画領域高さ
 * @param {*} obsInfo 観測所情報
 */
function addSwstageGraphBorder(parent, left, top, width, height, obsInfo) {
  let spec = obsInfo.spec.level;
  let max = spec.max;
  let min = spec.min;

  let bottom = top + height;
  let right = left + width;
  let ys = height / (max - min);
  let y;
  let tag;

  if (spec.start != null && spec.start <= max && spec.start >= min) {
    y = bottom - ys * (spec.start - min);
    tag = libCG.makeBorderGraphSvgTag(
      left,
      y,
      right,
      y,
      "kwg-waterlevel-start-border"
    );
    parent.appendChild(tag);
  }
  if (spec.danger != null && spec.danger <= max && spec.danger >= min) {
    y = bottom - ys * (spec.danger - min);
    tag = libCG.makeBorderGraphSvgTag(
      left,
      y,
      right,
      y,
      "kwg-waterlevel-danger-border"
    );
    parent.appendChild(tag);
  }
  if (spec.flood != null && spec.flood <= max && spec.flood >= min) {
    y = bottom - ys * (spec.flood - min);
    tag = libCG.makeBorderGraphSvgTag(
      left,
      y,
      right,
      y,
      "kwg-waterlevel-flood-border"
    );
    parent.appendChild(tag);
  }
}

/**
 * 危機管理水位ツールチップ情報生成
 * @param {*} obsInfo 観測所情報
 * @param {*} data 観測値
 * @param {*} number 要素番号
 */
function makeSwstageTip(obsInfo, data) {
  let spec = obsInfo.spec.level;
  // 観測日時
  let tip = data.date + " " + data.time;
  // 観測値
  tip = tip + str.CRLF + strStageTitle + str.Sept;
  if (data.level != null) {
    tip = tip + data.level + spec.unit;
  } else {
    tip = tip + str.None;
  }

  return tip;
}

/********** **********/

/**
 * SVG左軸取得
 * @param obsInfo 観測所情報
 *    value[]: 観測値
 *      date: 観測日
 *      time: 観測時間
 *      level: 水位レベル
 *    spec: 観測情報
 *      level: 水位レベル（堤防天端を0.0mとした水位）
 *        max: 水位最大値
 *        flood: 氾濫開始水位（通常は0.0m）
 *        danger: 危険水位
 *        start: 観測開始水位
 *        min: 水位最小値（通常は河床-1.0m）
 *        title: 水位レベルラベル
 *        unit: 水位レベル単位
 * @param viewProp 描画プロパティ
 *    width: 親要素の幅
 *    height: 親要素の高さ
 *    isExp: 拡大/全体表示（true=拡大, false=全体）
 * @return SVG左ラベル
 */
function getSvgLeftLabel(obsInfo, viewProp) {
  // svgタグ生成
  let svgLeftLabel = libCG.makeLabelSvgTag(viewProp.width, viewProp.height);
  // gタグ生成
  addGLeftLabel(svgLeftLabel, obsInfo, viewProp, 0);

  return svgLeftLabel.outerHTML;
}

/**
 * 左ラベルグループ追加
 * @param {*} parent 親要素
 * @param {*} obsInfo 観測所情報
 * @param {*} viewProp 描画プロパティ
 * @param {*} offsetY 余白の高さ
 */
function addGLeftLabel(parent, obsInfo, viewProp, offsetY) {
  let spec = obsInfo.spec.level;
  let tip;
  // 水位左ラベル表示範囲
  let x = paddingLeft;
  let y = paddingTop;
  let w = libCG.computeLabelWidth() - paddingLeft;
  let h =
    libCG.computeHeight(viewProp.width, viewProp.height) -
    paddingTop -
    paddingBottom -
    offsetY;
  // gタグ生成
  let g = libCG.addLeftLabelGroupTag(
    parent,
    viewProp.width,
    viewProp.height,
    obsInfo
  );
  // 文字サイズ算出
  let style = String(styleLeftLabel);
  const sizeStage = libCG.computeTitleSize(
    viewProp.width,
    viewProp.height,
    baseStageTitleSize,
    baseStageTitleWeight
  );
  style = style.replace(/TITLESTAGEWEIGHT/g, sizeStage.weight);
  style = style.replace(/TITLESTAGESIZE/g, sizeStage.size + "px");
  // styleタグ追加
  libCG.addStyleTag(g, style);
  // 水位グラフ
  // 水位グラフ表示範囲
  y = paddingTop;
  h =
    libCG.computeHeight(viewProp.width, viewProp.height) -
    paddingTop -
    paddingBottom -
    offsetY;
  // 目盛タグ追加
  libCG.addLeftLabelYAxisLabelTag(
    g,
    x,
    y,
    w,
    h,
    spec.max,
    spec.min,
    stageLabelDisit,
    stageDivY,
    spaceY,
    "kwg-waterlevel-label"
  );
  // ラベルタグ追加
  libCG.addLeftLabelYAxisTitleTag(
    g,
    x,
    y,
    w,
    h,
    spec.title,
    spec.unit,
    "kwg-waterlevel-title"
  );
  // 観測開始水位ボーダーマーカータグ追加
  if (spec.start != null && spec.start <= spec.max && spec.start >= spec.min) {
    tip = strStageStart + str.Sept + spec.start + spec.unit;
    libCG.addLeftLabelYAxisMarkerTag(
      g,
      x,
      y,
      w,
      h,
      spec.max,
      spec.min,
      spec.start,
      tip,
      "kwg-waterlevel-start-marker"
    );
  }
  // 危険水位ボーダーマーカータグ追加
  if (
    spec.danger != null &&
    spec.danger <= spec.max &&
    spec.danger >= spec.min
  ) {
    tip = strStageDanger + str.Sept + spec.danger + spec.unit;
    libCG.addLeftLabelYAxisMarkerTag(
      g,
      x,
      y,
      w,
      h,
      spec.max,
      spec.min,
      spec.danger,
      tip,
      "kwg-waterlevel-danger-marker"
    );
  }
  // 氾濫開始水位ボーダーマーカータグ追加
  if (spec.flood != null && spec.flood <= spec.max && spec.flood >= spec.min) {
    tip = strStageFlood + str.Sept + spec.flood + spec.unit;
    libCG.addLeftLabelYAxisMarkerTag(
      g,
      x,
      y,
      w,
      h,
      spec.max,
      spec.min,
      spec.flood,
      tip,
      "kwg-waterlevel-flood-marker"
    );
  }

  return g;
}

/********** **********/

/**
 * SVG右軸取得
 * @param obsInfo 観測所情報
 *    value[]: 観測値
 *      date: 観測日
 *      time: 観測時間
 *      level: 水位レベル
 *    spec: 観測情報
 *      level: 水位レベル（堤防天端を0.0mとした水位）
 *        max: 水位最大値
 *        flood: 氾濫開始水位（通常は0.0m）
 *        danger: 危険水位
 *        start: 観測開始水位
 *        min: 水位最小値（通常は河床-1.0m）
 *        title: 水位レベルラベル
 *        unit: 水位レベル単位
 * @param viewProp 描画プロパティ
 *    width: 親要素の幅
 *    height: 親要素の高さ
 *    isExp: 拡大/全体表示（true=拡大, false=全体）
 * @return SVG右ラベル
 */
function getSvgRightLabel(obsInfo, viewProp) {
  // svgタグ生成
  let svgRightLabel = libCG.makeLabelSvgTag(viewProp.width, viewProp.height);
  // gタグ生成
  addGRightLabel(svgRightLabel, obsInfo, viewProp, 0);

  return svgRightLabel.outerHTML;
}

/**
 * 右ラベルグループ追加
 * @param {*} parent 親要素
 * @param {*} obsInfo 観測所情報
 * @param {*} viewProp 描画プロパティ
 * @param {*} offsetY 余白の高さ
 */
// eslint-disable-next-line
function addGRightLabel(parent, obsInfo, viewProp, offsetY) {
  // gタグ生成
  let g = libCG.addRightLabelGroupTag(
    parent,
    viewProp.width,
    viewProp.height,
    obsInfo
  );

  return g;
}

/********** **********/

/**
 * SVG凡例取得
 * @param obsInfo 観測所情報
 *    value[]: 観測値
 *      date: 観測日
 *      time: 観測時間
 *      level: 水位レベル
 *    spec: 観測情報
 *      level: 水位レベル（堤防天端を0.0mとした水位）
 *        max: 水位最大値
 *        flood: 氾濫開始水位（通常は0.0m）
 *        danger: 危険水位
 *        start: 観測開始水位
 *        min: 水位最小値（通常は河床-1.0m）
 *        title: 水位レベルラベル
 *        unit: 水位レベル単位
 * @param viewProp 描画プロパティ
 *    width: 親要素の幅
 *    height: 親要素の高さ
 *    isExp: 拡大/全体表示（true=拡大, false=全体）
 * @return SVG凡例
 */
function getSvgLegend(obsInfo, viewProp) {
  let h = legendPadding + legendHeight;
  // svgタグ生成
  let svgLegend = libCG.makeLegendSvgTag(
    viewProp.width,
    viewProp.height,
    legendWidth + legendPadding,
    h
  );
  // gタグ追加
  addGLegend(svgLegend, obsInfo);

  return svgLegend.outerHTML;
}

/**
 * 凡例グループ追加
 * @param {*} parent 親要素
 * @param {*} obsInfo 観測所情報
 */
function addGLegend(parent, obsInfo) {
  let tag;
  let spec = obsInfo.spec;
  let w = legendWidth;
  let x = 0;
  let y = 5;
  let h = legendHeight;
  // gタグ追加
  let g = libCG.addLegendGroupTag(parent);
  // styleタグ追加
  libCG.addStyleTag(g, styleLegend);

  // 枠
  tag = libCG.makeLegendFrame(5, y, w, h, "kwg-legend-rect");
  g.appendChild(tag);
  // 凡例
  y = y + 70;
  y = libCG.addLegendTitle(
    g,
    x,
    y,
    strStageTitle,
    "kwg-legend-label",
    spec.level.unit,
    "kwg-legend-label-unit"
  );
  y = libCG.addLegendLineGraph(
    g,
    x,
    y,
    "kwg-waterlevel-normal",
    strStageLevel,
    "kwg-legend-label"
  );
  y = libCG.addLegendLineGraph(
    g,
    x,
    y,
    "kwg-waterlevel-flood-border",
    strStageFlood,
    "kwg-legend-label"
  );
  y = libCG.addLegendLineGraph(
    g,
    x,
    y,
    "kwg-waterlevel-danger-border",
    strStageDanger,
    "kwg-legend-label"
  );
  y = libCG.addLegendLineGraph(
    g,
    x,
    y,
    "kwg-waterlevel-start-border",
    strStageStart,
    "kwg-legend-label"
  );

  return g;
}

/********** **********/

/**
 * 画像グラフ取得
 * @param obsInfo 観測所情報
 *    value[]: 観測値
 *      date: 観測日
 *      time: 観測時間
 *      level: 水位レベル
 *    spec: 観測情報
 *      obs: 観測所情報
 *        obsNm: 観測所名
 *        rsysNm: 水系名
 *        rvrNm: 河川名
 *        obsDt: 観測日時
 *      level: 水位レベル（堤防天端を0.0mとした水位）
 *        max: 水位最大値
 *        flood: 氾濫開始水位（通常は0.0m）
 *        danger: 危険水位
 *        start: 観測開始水位
 *        min: 水位最小値（通常は河床-1.0m）
 *        title: 水位レベルラベル
 *        unit: 水位レベル単位
 *        obsCycl:観測周期(分)
 * @param viewProp 描画プロパティ
 *    width: 親要素の幅
 *    height: 親要素の高さ
 *    isExp: 拡大/全体表示（true=拡大, false=全体）
 *    isInfo: 観測所情報表示（true=表示, false=非表示）
 *    isLegend: 凡例表示（true=表示, false=非表示）
 * @param callback 画像生成後に呼び出すコールバック関数
 * @return SVGオブジェクト
 */
function getImageGraph(obsInfo, viewProp, callback) {
  let g;
  let offsetX;
  let offsetY = 0;
  let lw = viewProp.isLegend ? legendWidth + legendPadding : 0;
  let graphProp = Object.assign({}, viewProp);
  graphProp.width = libCG.computeImageGraphWidth(viewProp.width, lw);

  // svgタグ生成
  let svgImage = libCG.makeImageSvgTag(viewProp.width, viewProp.height, lw);

  // 観測所情報追加
  if (viewProp.isInfo && obsInfo.spec.obs != null) {
    let obs = obsInfo.spec.obs;
    offsetY = libCG.addObsInfo(
      svgImage,
      obs.obsNm,
      obs.rsysNm,
      obs.rvrNm,
      obs.obsDt
    );
  }

  // 左ラベル追加
  g = addGLeftLabel(svgImage, obsInfo, graphProp, offsetY);
  g.setAttribute("transform", "translate(0," + offsetY + ")");

  // グラフ追加
  g = addGGraph(svgImage, obsInfo, graphProp, true, offsetY);
  offsetX = libCG.computeLabelWidth();
  g.setAttribute("transform", "translate(" + offsetX + "," + offsetY + ")");

  // 右ラベル追加
  g = addGRightLabel(svgImage, obsInfo, graphProp, offsetY);
  offsetX = libCG.computeLabelWidth() + libCG.computeGraphWidth(false);
  g.setAttribute("transform", "translate(" + offsetX + "," + offsetY + ")");

  // 凡例追加
  if (viewProp.isLegend) {
    graphProp.width = libCG.computeImageLegendWidth(
      viewProp.width,
      legendWidth
    );
    g = addGLegend(svgImage, obsInfo, graphProp);
    offsetX = libCG.computeLabelWidth() * 2 + libCG.computeGraphWidth(false);
    g.setAttribute(
      "transform",
      "translate(" + offsetX + "," + (offsetY + 50) + ")"
    );
  }

  // 画像要素返却
  if (callback != null) {
    callback(svgImage);
  }

  return svgImage;
}
